import { useMutation } from "@apollo/client";
import { FC, useEffect, useState } from "react";
import {
  PlaidLinkError,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccessMetadata,
  usePlaidLink,
} from "react-plaid-link";
import {
  COMPLETE_VERIFICATION_MUTATION,
  completeVerificationResponse,
  CompleteVerificationVariables,
  FAIL_VERIFICATION_MUTATION,
  FailVerificationVariables,
  START_VERIFICATION_MUTATION,
  StartVerificationResponse,
  StartVerificationVariables,
} from "./verification.service";
import { VerificationStatus } from "../../shared/interfaces/iform";
import {
  VerificationLabel,
  VerificationButton,
  VerificationContainer,
  VerificationResultImage,
} from "./verification.styles";
import { success, error } from "../../shared/utils/pathConstants";
import { Loader } from "@mantine/core";
import moment from "moment";

export type IVerificationProps = {
  id: string;
  handleSetStepCanAdvance: (id: string, flag: boolean) => void;
  formFields: {
    dateOfBirth: string;
    email: string;
    firstName: string;
    lastName: string;
    ssn: string;
    phoneNumber: string;
    verificationStatus: VerificationStatus;
  };
  handleVerificationChange: (verificationStatus: VerificationStatus) => void;
  handleFieldChange: (label: string, value: string) => void;
  template: "selfApply" | "advisorPrefill"
};

const Verification: FC<IVerificationProps> = ({
  id,
  handleSetStepCanAdvance,
  handleVerificationChange,
  handleFieldChange,
  formFields,
  template
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [startVerification, { data }] = useMutation<
    StartVerificationResponse,
    StartVerificationVariables
  >(START_VERIFICATION_MUTATION, {
    variables: {
      startVerificationInput: {
        template: template,
        dateOfBirth: formFields.dateOfBirth && moment(formFields.dateOfBirth).format("YYYY-MM-DD") ,
        email: formFields.email,
        name: formFields.firstName,
        lastName: formFields.lastName,
        ssn: formFields.ssn,
        phoneNumber: formFields.phoneNumber,
      },
    },
  });
  const [failVerification] = useMutation<any, FailVerificationVariables>(
    FAIL_VERIFICATION_MUTATION,
  );
  const [completeVerification] = useMutation<
    completeVerificationResponse,
    CompleteVerificationVariables
  >(COMPLETE_VERIFICATION_MUTATION);

  useEffect(() => {
    startVerification();
  }, []);

  useEffect(() => {
    setLoading(false);

    if (data) {
      handleVerificationChange(
        VerificationStatus[
          data?.startVerification.status as VerificationStatus
        ],
      );
      handleFieldChange("plaidClientId", data.startVerification.plaidClientId);

      if (data.startVerification.status === VerificationStatus.ERROR)
        handleSetStepCanAdvance(id, true);
    }
  }, [data]);

  const onSuccess = async (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => {
    console.log("OnSuccess", publicToken, metadata);

    const { data: completeVerificationResponse } = await completeVerification({
      variables: {
        completeVerificationInput: {
          id: data!.startVerification.id,
          linkSessionId: metadata.link_session_id,
          publicToken: publicToken,
        },
      },
    });

    if (completeVerificationResponse?.completeVerification.status) {
      setLoading(false);
      handleVerificationChange(
        completeVerificationResponse?.completeVerification.status,
      );
      handleSetStepCanAdvance(id, true);
    }
  };

  const onExit = async (
    error: PlaidLinkError | null,
    metadata: PlaidLinkOnExitMetadata,
  ) => {
    console.info("onExit", error, metadata);
    if (!data) return;

    await failVerification({
      variables: {
        failVerificationInput: {
          id: data?.startVerification.id,
          errorCode: error?.error_code,
          errorMessage:
            error?.error_message || "Failed to render the Plaid component",
          errorType: error?.error_type,
          linkSessionId: metadata.link_session_id,
        },
      },
    });

    setLoading(false);
    handleSetStepCanAdvance(id, true);
    handleVerificationChange(VerificationStatus.FAILED);
  };

  const { open, ready } = usePlaidLink({
    token: data?.startVerification.linkToken || null,
    onSuccess: onSuccess,
    onExit: onExit,
    onLoad: () => setLoading(false),
  });

  useEffect(() => {
    if (ready) setLoading(false);
  }, [ready]);

  const triggerVerification = () => {
    open();
  };

  const getVerificationLabel = (
    verificationStatus: VerificationStatus,
  ): string => {
    switch (verificationStatus) {
      case VerificationStatus.PENDING:
        return "Please verify your identity before continuing";
      case VerificationStatus.SUCCESS:
        return "Your identity has been successfully verified";
      case VerificationStatus.FAILED:
      case VerificationStatus.ERROR:
        return "Your identity verification has failed, please contact an advisor";
    }

    return "";
  };

  if (loading)
    return (
      <VerificationContainer>
        <Loader />
      </VerificationContainer>
    );

  return (
    <VerificationContainer>
      {formFields.verificationStatus === VerificationStatus.SUCCESS && (
        <VerificationResultImage src={success} alt="Success" />
      )}
      {formFields.verificationStatus === VerificationStatus.ERROR ||
      formFields.verificationStatus === VerificationStatus.FAILED ? (
        <VerificationResultImage src={error} alt="Failed" />
      ) : null}
      <VerificationLabel>
        {getVerificationLabel(formFields.verificationStatus)}
      </VerificationLabel>
      {formFields.verificationStatus === VerificationStatus.PENDING && (
        <VerificationButton
          onClick={triggerVerification}
          disableElevation
          disabled={!ready}
          name="start"
        >
          Start Verification
        </VerificationButton>
      )}
    </VerificationContainer>
  );
};

export default Verification;
